import type {DiffAnchor, DiffLineType} from '@github-ui/diffs/types'
import {clsx} from 'clsx'
import type React from 'react'
import {memo, useCallback, useRef} from 'react'
import {cellIdFrom, getLineBackgroundColor} from '../../helpers/line-helpers'
import {useStartConversationDialog} from '../../hooks/use-start-conversation-dialog'
import type {ClientDiffLine, FileDiffReference, DiffLineWithSelectionInfo} from '../../types'
import type {MarkersStatusAction} from '../../hooks/use-markers-dialog-status'
import {useDiffLinesActions, useGetFirstLineSelectionOnDrag, useGetSelectedLineRange} from '../../stores/selectors'
import {handleLineSelectionEffects} from '../../helpers/line-selection'
import {diffLineKey} from '../../stores/slices/helpers/diff-lines-slice-helpers'
import {Cell} from './Cell'
import {useEnterDialogMode} from '../../hooks/use-enter-dialog-mode'

const DIMMED_LINE_NUMBER_TYPES: DiffLineType[] = ['CONTEXT', 'INJECTED_CONTEXT']

function lineHasThreads(line: DiffLineWithSelectionInfo, isLeftSide: boolean | undefined, isSplitDiff: boolean) {
  if (line.type === 'CONTEXT' && isLeftSide && isSplitDiff) return false
  return !!line.threadsData?.threads?.length || !!line.annotationsData?.annotations?.length
}

type LineNumberCellProps = React.PropsWithChildren<{
  columnIndex: number
  copilotChatReference?: FileDiffReference
  contentColumnIndex: number
  contentRef: React.RefObject<HTMLTableCellElement>
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  dispatchMarkersStatus: React.Dispatch<MarkersStatusAction>
  filePath: string
  handleDiffSideCellSelectionBlocking: (event: React.MouseEvent) => void
  isLeftSide: boolean
  isRowSelected: boolean
  isSplit: boolean
  rowId: string
}>

/**
 * Renders a line number cell
 */
export const LineNumberCell = memo(LineNumberCellUnmemoized)
export function LineNumberCellUnmemoized({
  children,
  columnIndex,
  diffAnchor,
  diffLine,
  handleDiffSideCellSelectionBlocking,
  isLeftSide,
  isRowSelected,
  isSplit,
  filePath,
  copilotChatReference,
  contentRef,
  contentColumnIndex,
  dispatchMarkersStatus,
  rowId,
  ...rest
}: LineNumberCellProps) {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const line = diffLine as DiffLineWithSelectionInfo
  const getfirstLineSelectionOnDrag = useGetFirstLineSelectionOnDrag()
  const {addLineToSelection} = useDiffLinesActions()

  const handleMouseEnterCell = useCallback(
    (e: React.MouseEvent) => {
      if (e.buttons !== 1) return
      const firstLineSelectionOnDrag = getfirstLineSelectionOnDrag()
      if (firstLineSelectionOnDrag) {
        addLineToSelection(
          {
            lineKey: diffLineKey(line),
            orientation: isLeftSide ? 'left' : 'right',
          },
          handleLineSelectionEffects,
        )
      }
    },
    [getfirstLineSelectionOnDrag, addLineToSelection, line, isLeftSide],
  )

  const {selectLine} = useDiffLinesActions()
  const getSelectedLineRange = useGetSelectedLineRange()

  const handleMouseDownOnNumberCell = (e: React.MouseEvent) => {
    const selectedLineRange = getSelectedLineRange()
    // We only handle adding lines to selection on shift + click for onMouseDown event handlers.
    if (selectedLineRange && e.shiftKey) {
      addLineToSelection(
        {
          lineKey: diffLineKey(line),
          orientation: isLeftSide ? 'left' : 'right',
        },
        handleLineSelectionEffects,
      )
      return
    }

    const lineNumber = isLeftSide ? line.left : line.right

    if (lineNumber) {
      selectLine(
        {
          lineKey: diffLineKey(line),
          firstLineSelectionOnDrag: {
            orientation: isLeftSide ? 'left' : 'right',
            lineNumber,
            shouldStartConversation: false,
            lineKey: diffLineKey(line),
          },
        },
        handleLineSelectionEffects,
      )
    }
  }

  const key = `LineNumberCell-left:${line.left}-right:${line.right}-leftSide:${isLeftSide}`

  const contentCellId = cellIdFrom(rowId, contentColumnIndex)
  const enterDialogMode = useEnterDialogMode(contentCellId, contentRef, dispatchMarkersStatus)

  const {startNewConversation} = useStartConversationDialog({
    cellId: contentCellId,
    onOpenDialog: enterDialogMode,
  })
  const cellId = cellIdFrom(rowId, columnIndex)

  return (
    <Cell
      key={key}
      ref={cellRef}
      cellId={cellId}
      className={clsx(
        'diff-line-number position-relative',
        DIMMED_LINE_NUMBER_TYPES.includes(line.type) && 'diff-line-number-neutral',
      )}
      columnIndex={columnIndex}
      diffAnchor={diffAnchor}
      diffLine={diffLine}
      data-first-unified-line-number-cell={!isSplit && columnIndex === 0}
      enterDialogMode={enterDialogMode}
      handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
      hasThreads={lineHasThreads(line, isLeftSide, isSplit)}
      style={{
        backgroundColor: getLineBackgroundColor(line.type, true, isRowSelected),
        textAlign: 'center',
      }}
      handleDiffCellMouseDown={handleMouseDownOnNumberCell}
      handleStartConversation={startNewConversation}
      isLeftSide={isLeftSide}
      isRowSelected={isRowSelected}
      isSplit={isSplit}
      onMouseEnter={handleMouseEnterCell}
      {...rest}
    >
      <code>{children}</code>
    </Cell>
  )
}

try{ LineNumberCell.displayName ||= 'LineNumberCell' } catch {}
try{ LineNumberCellUnmemoized.displayName ||= 'LineNumberCellUnmemoized' } catch {}